/**
 * Modal for inviting new users to the team
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { teamApi } from '@/api/team';
import { useAuth } from '@/hooks/useAuth';
import type { InviteUserRequest, TeamUser, TeamUserResponse } from '@/types/team';
import type { InfiniteData } from '@tanstack/react-query';

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => void;
}

interface TeamRoleRow {
  team_uuid: string;
  role: 'SERVICE_MANAGER' | 'SERVICE_MEMBER';
}

export const InviteUserModal: React.FC<InviteUserModalProps> = ({ open, onClose, onSuccess }) => {
  const { user: currentUser } = useAuth();
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [formData, setFormData] = useState<{
    name: string;
    email: string;
    role: 'user' | 'staff' | 'superuser';
    unipile_id: string;
  }>({
    name: '',
    email: '',
    role: 'user',
    unipile_id: '',
  });
  const [teamRolesInput, setTeamRolesInput] = useState<TeamRoleRow[]>([]);
  const [errors, setErrors] = useState<{ email?: string; role?: string; team_roles?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [selectedExistingUser, setSelectedExistingUser] = useState<TeamUser | null>(null);
  const trimmedExistingUserSearch = userSearch.trim();
  const shouldSearchExistingUsers = trimmedExistingUserSearch.length > 0;

  const queryClient = useQueryClient();

  // For admins, fetch all teams; for non-admins, fetch teams they own or manage
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['invite-user-teams', currentUser?.key],
    queryFn: () =>
      teamApi.getTeams(
        currentUser?.is_staff || currentUser?.is_superuser
          ? { max_results: 1000 }
          : { max_results: 1000, manager_or_owner_uuid: currentUser?.key }
      ),
    enabled: open && !!currentUser,
  });

  const availableProjects =
    projectsData?.output?.items?.map((p: any) => ({
      uuid: p.key,
      name: p.name || p.key,
    })) || [];

  // Existing users search - enabled when modal is open and mode is existing
  const {
    data: existingUsersPages,
    isLoading: isLoadingUsers,
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: hasNextUsersPage,
    isFetchingNextPage: isFetchingNextUsersPage,
  } = useInfiniteQuery<
    TeamUserResponse,
    Error,
    InfiniteData<TeamUserResponse, string | undefined>,
    [string, string],
    string | undefined
  >({
    queryKey: ['invite-existing-users-search-all', trimmedExistingUserSearch] as [
      string,
      string,
    ],
    queryFn: ({ pageParam }) =>
      teamApi.searchAllUsers({
        page_token: pageParam,
        max_results: 10,
        search: trimmedExistingUserSearch,
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token,
    initialPageParam: undefined,
    enabled: open && mode === 'existing' && shouldSearchExistingUsers,
  });

  const existingUsers: TeamUser[] =
    existingUsersPages?.pages.flatMap((page: TeamUserResponse) => page.output.items) ?? [];

  const existingUsersLoadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      !shouldSearchExistingUsers ||
      !existingUsersLoadMoreRef.current ||
      !hasNextUsersPage ||
      !fetchNextUsersPage
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextUsersPage && !isFetchingNextUsersPage) {
          fetchNextUsersPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(existingUsersLoadMoreRef.current);
    return () => observer.disconnect();
  }, [
    hasNextUsersPage,
    isFetchingNextUsersPage,
    fetchNextUsersPage,
    shouldSearchExistingUsers,
  ]);

  const inviteMutation = useMutation({
    mutationFn: teamApi.inviteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-users'] });
      queryClient.invalidateQueries({ queryKey: ['pending-invites-count'] });
      if (onSuccess) {
        onSuccess('Invitation sent successfully');
      }
      setMode('new');
      setFormData({ name: '', email: '', role: 'user', unipile_id: '' });
      setTeamRolesInput([]);
      setErrors({});
      setSubmitError(null);
      setUserSearch('');
      setSelectedExistingUser(null);
      onClose();
    },
    onError: (error: any) => {
      const responseData = error?.response?.data;
      const errorMessage =
        responseData?.error_messages?.[0] ||
        responseData?.detail ||
        responseData?.message ||
        'Failed to invite user';

      setSubmitError(errorMessage);
      if (mode === 'new') {
        setErrors((prev) => ({ ...prev, email: errorMessage }));
      }
    },
  });

  // Determine available system roles based on current user's role
  const getAvailableSystemRoles = (): ('user' | 'staff' | 'superuser')[] => {
    if (currentUser?.is_superuser) {
      return ['user', 'staff', 'superuser'];
    } else if (currentUser?.is_staff) {
      return ['user', 'staff'];
    }
    return ['user'];
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    
    // Validation
    const newErrors: { email?: string; role?: string; team_roles?: string } = {};
    
    // Validate team roles
    const validTeamRoles = teamRolesInput.filter(pr => pr.team_uuid && pr.role);
    if (teamRolesInput.length > 0 && validTeamRoles.length !== teamRolesInput.length) {
      newErrors.team_roles = 'All team roles must have both team and role selected';
    }
    // If user adds a row, team and role are required
    if (teamRolesInput.length > 0 && validTeamRoles.length === 0) {
      newErrors.team_roles = 'Please select a team and role';
    }

    if (mode === 'new') {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.role) {
        newErrors.role = 'Role is required';
      }
    } else if (mode === 'existing') {
      if (!selectedExistingUser) {
        newErrors.team_roles = 'Please select a user to add to teams';
      }
      if (validTeamRoles.length === 0) {
        newErrors.team_roles =
          newErrors.team_roles || 'Please select at least one team role';
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    if (mode === 'new') {
      const requestData: InviteUserRequest = {
        email: formData.email,
        name: formData.name || undefined,
        role: formData.role,
        unipile_id: formData.unipile_id || undefined,
        team_roles: validTeamRoles.length > 0 ? validTeamRoles : undefined,
      };
      inviteMutation.mutate(requestData);
    } else {
      if (!selectedExistingUser) return;
      const userUuid = (selectedExistingUser.uuid || selectedExistingUser.key) as string;
      const requestData: InviteUserRequest = {
        user_uuid: userUuid,
        team_roles: validTeamRoles,
      };
      inviteMutation.mutate(requestData);
    }
  };

  const handleClose = () => {
    if (!inviteMutation.isPending) {
      onClose();
      setMode('new');
      setFormData({ name: '', email: '', role: 'user', unipile_id: '' });
      setTeamRolesInput([]);
      setErrors({});
      setSubmitError(null);
      setUserSearch('');
      setSelectedExistingUser(null);
    }
  };

  const addTeamRoleRow = () => {
    setTeamRolesInput([...teamRolesInput, { team_uuid: '', role: 'SERVICE_MEMBER' }]);
  };

  const removeTeamRoleRow = (index: number) => {
    setTeamRolesInput(teamRolesInput.filter((_, i) => i !== index));
  };

  const updateTeamRole = (index: number, field: 'team_uuid' | 'role', value: string) => {
    const updated = [...teamRolesInput];
    updated[index] = { ...updated[index], [field]: value };
    setTeamRolesInput(updated);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Invite Team Member
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Send an invitation to join your team
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_, v) => {
                  if (v !== null) {
                    setMode(v);
                    setErrors({});
                    setSubmitError(null);
                    setSelectedExistingUser(null);
                    setUserSearch('');
                  }
                }}
                aria-label="user selection mode"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                  },
                }}
              >
                <ToggleButton value="existing" aria-label="select existing user">
                  <PersonIcon sx={{ mr: 1 }} />
                  Select Existing User
                </ToggleButton>
                <ToggleButton value="new" aria-label="create new user">
                  <PersonAddIcon sx={{ mr: 1 }} />
                  Create New User
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {mode === 'new' && (
              <>
                <TextField
                  label="Name (Optional)"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  fullWidth
                  disabled={inviteMutation.isPending}
                />

                <TextField
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, email: e.target.value }));
                    if (errors.email) {
                      setErrors((prev) => ({ ...prev, email: undefined }));
                    }
                    if (submitError) {
                      setSubmitError(null);
                    }
                  }}
                  error={!!errors.email}
                  helperText={errors.email}
                  fullWidth
                  required
                  disabled={inviteMutation.isPending}
                />
                
                <FormControl fullWidth error={!!errors.role}>
                  <InputLabel>System Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'user' | 'staff' | 'superuser' }))}
                    label="System Role"
                    disabled={inviteMutation.isPending}
                  >
                    {getAvailableSystemRoles().map(role => (
                      <MenuItem key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.role && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.role}
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  label="Unipile ID (Optional)"
                  value={formData.unipile_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, unipile_id: e.target.value }))}
                  fullWidth
                  disabled={inviteMutation.isPending}
                  helperText="Unipile identifier for the user"
                />
              </>
            )}

            {mode === 'existing' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  placeholder="Search users by name or email..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  fullWidth
                  disabled={inviteMutation.isPending}
                  helperText={
                    !shouldSearchExistingUsers
                      ? 'Type at least one character to search for a user'
                      : undefined
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ maxHeight: 260, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {!shouldSearchExistingUsers ? (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Start typing to look up existing team members.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {isLoadingUsers && existingUsers.length === 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      )}
                      <List dense>
                        {existingUsers.map((user) => {
                          const label = user.name || user.email || 'User';
                          const secondary = user.email && user.name ? user.email : '';
                          const isSelected = selectedExistingUser?.key === user.key;
                          return (
                            <ListItemButton
                              key={user.key}
                              selected={isSelected}
                              onClick={() => setSelectedExistingUser(user)}
                            >
                              <ListItemText primary={label} secondary={secondary} />
                            </ListItemButton>
                          );
                        })}
                      </List>
                      <div ref={existingUsersLoadMoreRef} style={{ height: 16 }} />
                      {isFetchingNextUsersPage && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                          <CircularProgress size={18} />
                        </Box>
                      )}
                      {existingUsers.length === 0 && !isLoadingUsers && (
                        <Box sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            No users found.
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Team Roles <Typography component="span" color="error">*</Typography>
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addTeamRoleRow}
                  size="small"
                  disabled={inviteMutation.isPending || isLoadingProjects}
                >
                  Add Team Role
                </Button>
              </Box>

              {isLoadingProjects && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {teamRolesInput.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  At least one team role is required. Click "Add Team Role" to get started.
                </Typography>
              )}

              {teamRolesInput.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {teamRolesInput.map((teamRole, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <FormControl sx={{ flex: 1 }} size="small" required error={!!errors.team_roles}>
                        <InputLabel>Team *</InputLabel>
                        <Select
                          value={teamRole.team_uuid}
                          onChange={(e) => {
                            updateTeamRole(index, 'team_uuid', e.target.value);
                            // Clear error when team is selected
                            if (errors.team_roles) {
                              setErrors(prev => ({ ...prev, team_roles: undefined }));
                            }
                          }}
                          label="Team *"
                          disabled={inviteMutation.isPending || isLoadingProjects}
                        >
                          {availableProjects.map((project: { uuid: string; name: string }) => (
                            <MenuItem key={project.uuid} value={project.uuid}>
                              {project.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl sx={{ minWidth: 180 }} size="small" required error={!!errors.team_roles}>
                        <InputLabel>Role *</InputLabel>
                        <Select
                          value={teamRole.role}
                          onChange={(e) => {
                            updateTeamRole(index, 'role', e.target.value);
                            // Clear error when role is selected
                            if (errors.team_roles) {
                              setErrors(prev => ({ ...prev, team_roles: undefined }));
                            }
                          }}
                          label="Role *"
                          disabled={inviteMutation.isPending}
                        >
                          <MenuItem value="SERVICE_MANAGER">Service Manager</MenuItem>
                          <MenuItem value="SERVICE_MEMBER">Service Member</MenuItem>
                        </Select>
                      </FormControl>
                      <IconButton
                        onClick={() => removeTeamRoleRow(index)}
                        disabled={inviteMutation.isPending}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {errors.team_roles && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.team_roles}
                </Typography>
              )}

              {availableProjects.length === 0 && !isLoadingProjects && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No teams available. You need to be an owner or SERVICE_MANAGER of at least one team to assign team roles.
                </Typography>
              )}
            </Box>
            
            {inviteMutation.isError && (
              <Alert severity="error">
                {submitError || 'Failed to invite user'}
              </Alert>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleClose}
            disabled={inviteMutation.isPending}
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              inviteMutation.isPending ||
              (mode === 'new' && (!formData.email || !formData.role)) ||
              (mode === 'existing' && !selectedExistingUser)
            }
            sx={{ minWidth: 100 }}
          >
            {inviteMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              mode === 'new' ? 'Create & Invite User' : 'Update User Team Roles'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
